// The new joiner's page. Opened from the welcome email with a one-time token,
// outside AuthProvider — they have no portal account.
//
// Three steps, shown one at a time so nobody has to work out what to do next:
// read what IT set up → confirm the checklist → take the policy quiz.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
const card = { background:'#fff', border:'1px solid #e4e7ee', borderRadius:10, padding:20, marginBottom:16 };
const page = { minHeight:'100vh', background:'#f5f6fa', padding:'32px 16px', fontFamily:'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', color:'#1b2440' };
const wrap = { maxWidth:760, margin:'0 auto' };
const btn  = { padding:'10px 18px', borderRadius:7, border:'none', background:'#2b58d4', color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' };
const btnOff = { ...btn, background:'#c8cedd', cursor:'not-allowed' };

export default function OnboardingStaff({ token }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get(`/onboard/${token}`)
      .then(r => { setD(r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.error || 'This link could not be opened.'));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const loadQuiz = useCallback(() => {
    api.get(`/onboard/${token}/quiz`).then(r => setQuiz(r.data.questions)).catch(()=>{});
  }, [token]);
  useEffect(() => { if (d?.quiz_unlocked && !d.quiz_passed) loadQuiz(); }, [d, loadQuiz]);

  if (err) return <div style={page}><div style={wrap}><div style={card}>
    <h2 style={{ marginTop:0 }}>IT Induction</h2>
    <p style={{ color:'#b3261e' }}>{err}</p>
  </div></div></div>;
  if (!d) return <div style={page}><div style={wrap}><div style={card}>Loading…</div></div></div>;

  const toggle = async (item, done) => {
    setD(p => ({ ...p, checklist: p.checklist.map(i => i.id === item.id ? { ...i, staff_done: done } : i) }));
    try { await api.post(`/onboard/${token}/confirm`, { item_id: item.id, done }); }
    catch (e) { load(); }
  };

  const submitChecklist = async () => {
    setBusy(true); setErr('');
    try { await api.post(`/onboard/${token}/submit-checklist`); load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not submit'); }
    finally { setBusy(false); }
  };

  const submitQuiz = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/onboard/${token}/quiz`, { answers });
      setResult(r.data); load();
      if (!r.data.passed) { setAnswers({}); loadQuiz(); }
    } catch (e) { setErr(e.response?.data?.error || 'Could not submit the quiz'); }
    finally { setBusy(false); }
  };

  const allTicked = d.checklist.every(i => i.staff_done);
  const step = d.quiz_passed ? 3 : d.checklist_done ? 2 : 1;

  return (
    <div style={page}><div style={wrap}>

      <div style={{ ...card, background:'#101c3d', color:'#fff', border:'none' }}>
        <div style={{ fontSize:12, letterSpacing:1.5, color:'#c9962b', fontWeight:700 }}>TIPS GLOBEDUCATE · IT DEPARTMENT</div>
        {/* Explicit colour: a global h1 rule would otherwise paint this navy
            on navy and the greeting disappears. */}
        <h1 style={{ fontSize:26, margin:'8px 0 4px', color:'#fff' }}>Welcome, {d.staff_name}</h1>
        <div style={{ color:'#bfc8de', fontSize:14 }}>
          {d.campus_name}{d.department_name ? ` · ${d.department_name}` : ''} · Reference {d.ref}
        </div>
      </div>

      {/* Progress */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['Your account', 'Confirm checklist', 'Policy quiz'].map((label, i) => (
          <div key={label} style={{ flex:1, padding:'8px 10px', borderRadius:7, fontSize:12.5, fontWeight:600,
            background: step > i ? '#dff3e6' : step === i + 1 ? '#fff' : '#eceef4',
            border: `1px solid ${step === i + 1 ? '#2b58d4' : '#e4e7ee'}`,
            color: step > i ? '#1b6b3a' : '#1b2440' }}>
            {step > i + 1 ? '✓ ' : `${i + 1}. `}{label}
          </div>
        ))}
      </div>

      {d.quiz_passed && (
        <div style={{ ...card, background:'#dff3e6', border:'1px solid #9ad4b2' }}>
          <b>All done — thank you.</b>
          <div style={{ fontSize:13.5, marginTop:4 }}>
            Your induction is complete and has been recorded. IT will close the request.
          </div>
        </div>
      )}

      {/* What IT has set up */}
      <div style={card}>
        <h3 style={{ marginTop:0, fontSize:16 }}>Your school account</h3>
        {/* Stacked rather than a two-column table: a school email is long
            enough to run off the side of a phone. */}
        {[['School email', <b style={{ wordBreak:'break-word' }}>{d.workspace_email || '—'}</b>],
          d.joining_date && ['Joining date', new Date(d.joining_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })],
          d.drive_folder_url && ['Your Drive folder',
            <a href={d.drive_folder_url} target="_blank" rel="noopener noreferrer">Open folder</a>],
        ].filter(Boolean).map(([k, v]) => (
          <div key={k} style={{ padding:'6px 0', borderBottom:'1px solid #f0f2f7' }}>
            <div style={{ fontSize:12.5, color:'#5a6478' }}>{k}</div>
            <div style={{ fontSize:14.5, marginTop:1 }}>{v}</div>
          </div>
        ))}
        <p style={{ fontSize:12.5, color:'#5a6478', marginBottom:0 }}>
          Your password was sent separately to your personal email address. Please change it when you first sign in,
          and never share it with anyone — including IT.
        </p>
      </div>

      {/* Reading */}
      {(d.policies.length > 0 || d.documents.length > 0) && (
        <div style={card}>
          <h3 style={{ marginTop:0, fontSize:16 }}>Please read before the quiz</h3>
          {d.policies.map(p => (
            <div key={`p${p.id}`} style={{ fontSize:14, padding:'5px 0', borderBottom:'1px solid #f0f2f7' }}>
              📋 <b>{p.title}</b> <span style={{ color:'#5a6478', fontSize:12.5 }}>v{p.version_no}</span>
            </div>
          ))}
          {d.documents.map(x => (
            <div key={`d${x.id}`} style={{ fontSize:14, padding:'5px 0', borderBottom:'1px solid #f0f2f7' }}>
              📄 {x.title}
            </div>
          ))}
          <p style={{ fontSize:12.5, color:'#5a6478', margin:'10px 0 0' }}>
            Your IT admin went through these with you during induction. Ask them for a copy of anything you need again.
          </p>
        </div>
      )}

      {/* Step 2 — checklist */}
      <div style={card}>
        <h3 style={{ marginTop:0, fontSize:16 }}>Confirm what you received</h3>
        {d.checklist_done ? (
          <div style={{ fontSize:14, color:'#1b6b3a' }}>✓ Submitted — thank you.</div>
        ) : (<>
          <p style={{ fontSize:13, color:'#5a6478', marginTop:0 }}>
            Tick each item you have actually received or been shown. If something is missing, leave it unticked
            and tell your IT admin — do not tick it to move on.
          </p>
          {d.checklist.map(i => (
            // className="opt" — the portal's own class for a clickable choice.
            // A plain <label> picks up the field-caption rule and comes out
            // uppercased and letter-spaced.
            <label className="opt" key={i.id} style={{ alignItems:'flex-start', padding:'9px 0',
              borderBottom:'1px solid #f0f2f7', fontSize:14 }}>
              <input type="checkbox" checked={!!i.staff_done} style={{ marginTop:3 }}
                     onChange={e => toggle(i, e.target.checked)} />
              <span>{i.label}</span>
            </label>
          ))}
          <button style={allTicked && !busy ? { ...btn, marginTop:14 } : { ...btnOff, marginTop:14 }}
                  disabled={!allTicked || busy} onClick={submitChecklist}>
            {busy ? 'Submitting…' : 'Submit checklist'}
          </button>
          {!allTicked && <div style={{ fontSize:12.5, color:'#5a6478', marginTop:8 }}>
            Tick every item to continue to the quiz.
          </div>}
        </>)}
      </div>

      {/* Step 3 — quiz */}
      {d.quiz_unlocked && !d.quiz_passed && (
        <div style={card}>
          <h3 style={{ marginTop:0, fontSize:16 }}>Policy quiz</h3>
          <p style={{ fontSize:13, color:'#5a6478', marginTop:0 }}>
            {d.quiz_pass_pct}% is needed to pass. You can retake it as many times as you need.
          </p>
          {result && !result.passed && (
            <div style={{ background:'#fdeaea', border:'1px solid #f0b4b4', borderRadius:7, padding:12, marginBottom:14, fontSize:13.5 }}>
              <b>You scored {result.score_pct}% — {result.pass_mark}% is needed.</b>
              {result.review.filter(x => !x.correct && x.explain).map(x => (
                <div key={x.id} style={{ marginTop:8 }}>• {x.question}<br/>
                  <span style={{ color:'#5a6478' }}>{x.explain}</span></div>
              ))}
            </div>
          )}
          {!quiz ? <div style={{ fontSize:13.5, color:'#5a6478' }}>Loading questions…</div> : (<>
            {quiz.map((q, qi) => (
              <div key={q.id} style={{ padding:'12px 0', borderBottom:'1px solid #f0f2f7' }}>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>{qi + 1}. {q.question}</div>
                {q.options.map((o, oi) => (
                  <label className="opt" key={oi} style={{ padding:'4px 0', fontSize:14 }}>
                    <input type="radio" name={`q${q.id}`} checked={answers[q.id] === oi}
                           onChange={() => setAnswers(a => ({ ...a, [q.id]: oi }))} />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            ))}
            <button style={Object.keys(answers).length === quiz.length && !busy ? { ...btn, marginTop:14 } : { ...btnOff, marginTop:14 }}
                    disabled={Object.keys(answers).length !== quiz.length || busy} onClick={submitQuiz}>
              {busy ? 'Submitting…' : 'Submit quiz'}
            </button>
          </>)}
        </div>
      )}

      <div style={{ textAlign:'center', fontSize:12, color:'#8a93a8', paddingBottom:24 }}>
        TIPS Globeducate · IT Department — if anything here is wrong, contact your campus IT admin.
      </div>
    </div></div>
  );
}
